import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import type { VariantType } from 'notistack';
import { forwardRef, useCallback, type MouseEvent } from 'react';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import { STYLED_SNACKBAR_SKIN_STYLES } from '../../../styles/components/styledSnackbar';
import { mergeSx } from '../../../utils/styles';
import type { StyledSnackbarContentProps } from './types';

const VARIANT_ICONS: Record<VariantType, typeof InfoOutlinedIcon> = {
  default: InfoOutlinedIcon,
  success: CheckCircleOutlineIcon,
  warning: WarningAmberOutlinedIcon,
  error: ErrorOutlineIcon,
  info: InfoOutlinedIcon,
};

const NeobetSnackbarContent = forwardRef<HTMLDivElement, StyledSnackbarContentProps>(
  ({ id, message, variant, action, closeLabel, onClose }, ref) => {
    const styles = useSkinStyles(STYLED_SNACKBAR_SKIN_STYLES);
    const VariantIcon = VARIANT_ICONS[variant] ?? InfoOutlinedIcon;
    const renderedAction = typeof action === 'function' ? action(id) : action;

    const handleActionClick = useCallback((event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
    }, []);

    const handleCloseClick = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onClose(event);
      },
      [onClose]
    );

    return (
      <Box ref={ref} onClick={onClose} sx={styles.root}>
        <Stack role='alert' direction='row' sx={mergeSx(styles.card, styles.variantCard[variant])}>
          <Box
            data-testid='styled-snackbar-variant-icon'
            data-variant={variant}
            sx={mergeSx(styles.icon, styles.variantIcon[variant])}
          >
            <VariantIcon aria-hidden />
          </Box>
          <Typography id='notistack-snackbar' component='div' sx={styles.message}>
            {message}
          </Typography>
          {renderedAction && (
            <Box sx={styles.actionArea} onClick={handleActionClick}>
              {renderedAction}
            </Box>
          )}
          <IconButton
            aria-label={closeLabel}
            data-testid='styled-snackbar-close'
            size='small'
            sx={styles.closeButton}
            onClick={handleCloseClick}
          >
            <CloseIcon sx={styles.closeIcon} />
          </IconButton>
        </Stack>
      </Box>
    );
  }
);

export default NeobetSnackbarContent;
